/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';

import { styleguideColors as COLORS, styleguideFontSize as FONT_SIZE, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily as FONT, withOpacity } from '@kaiizen/kai-shiki/core';
import { CheckIcon } from '@kaiizen/kai-shiki/react';

/* ── the approve chip ──
   Task, group and scope all offer the same verdict in the same chip; what
   differs is how many tasks it covers and what the button says. The chip and
   its three faces — offered, armed, given — live here so the three controls
   read as one control at three scales rather than as three controls that
   merely resemble each other. */

export const WRAPPER_STYLE = css`
  position: relative;
  display: inline-flex;
`;

/** The styleguide's small mono chip, in the approved green pair. */
export const APPROVE_CONTROL_STYLE = css`
  label: APPROVE_CONTROL_STYLE;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 26px;
  padding: 0 9px;
  border: 1.5px solid ${COLORS.success};
  background: ${COLORS.white};
  color: ${COLORS.success};
  font-family: ${FONT.mono};
  font-size: ${pxToRem(FONT_SIZE.monoMicro)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: 0.09em;
  text-transform: uppercase;
  white-space: nowrap;

  svg { flex-shrink: 0; }
`;

/**
 * Pressability follows the `disabled` attribute: enabled wears the pointer and
 * hover feedback, disabled mutes and shows the button is off limits. `disabled`
 * covers more than one reason now (no permission, nothing left to approve, a
 * write in flight) — only the last of those is transient, so the cursor
 * defaults to `not-allowed` here and SUBMITTING_STYLE below layers `wait` back
 * on top specifically while a write is actually in flight.
 */
export const APPROVE_BUTTON_STYLE = css`
  label: APPROVE_BUTTON_STYLE;
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease;

  &:hover { background: ${withOpacity(COLORS.success, 0.09)}; }
  &:focus-visible { outline: 2px solid ${COLORS.ink}; outline-offset: 1px; }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

/** Layered on top of APPROVE_BUTTON_STYLE while a write is actually in
 *  flight — the one disabled reason worth telling the reader to wait out. */
export const SUBMITTING_STYLE = css`
  label: SUBMITTING_STYLE;
  &:disabled {
    cursor: wait;
  }
`;

/**
 * Solid rather than tinted: the verdict, given. Whether it can still be pressed
 * is the mount's business — a task's chip is a button (click to unapprove, so it
 * keeps the button style's pointer), the bulk chip an inert div.
 */
export const APPROVED_CHIP_STYLE = css`
  label: APPROVED_CHIP_STYLE;
  background: ${COLORS.success};
  border-color: ${COLORS.success};
  color: ${COLORS.white};

  &:hover { background: #3F7A4F; }
`;

/**
 * Armed confirm: ink, because the next click commits. Green is the colour of
 * the verdict, so the step that hasn't reached one yet doesn't wear it.
 */
export const APPROVE_CONFIRM_STYLE = css`
  label: APPROVE_CONFIRM_STYLE;
  background: ${COLORS.ink};
  border-color: ${COLORS.ink};
  color: ${COLORS.white};

  /* Ink has nowhere brighter to go, so hover deepens instead of lightening. */
  &:hover { filter: none; background: #000; }
`;

export const NO_CURSOR_STYLE = css`
  label: NO_CURSOR_STYLE;
  cursor: default;
`;

export const KaiNavTaskApprovalCheckIcon = () => <CheckIcon size={12} color='currentColor' aria-hidden='true' />;
